import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  styled,
  SxProps,
  Theme
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import { Button, TextInput } from '@baseComponents/index';
import Utils from '../../utils/utils';
import { OfficialData } from '@networkRequest/officialsService';

export type MatchFilter = 'all' | 'unmatched' | 'matched';

interface UsersPanelProps {
  users: OfficialData[];
  loading: boolean;
  error: string;
  counts: { all: number; withPhoto: number; without: number };
  countsLoading: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  matchFilter: MatchFilter;
  onMatchFilterChange: (value: MatchFilter) => void;
  searchText: string;
  onSearchChange: (value: string) => void;
  onPageChange: (newPage: number) => void;
  isSelecting: boolean;
  matchingUserIds: number[];
  // Name of the photo currently being dragged from the images panel, or null.
  // When set, eligible rows light up as drop targets.
  draggedImageName: string | null;
  // Maps a photo to a user. Without an explicit image name, the parent uses the
  // currently selected image (click flow); the drag flow passes the dropped image.
  onMapPhoto: (officialId: number, imageName?: string) => void;
  onUnlinkPhoto: (official: OfficialData) => void;
}

const StyledToggleButton = styled(ToggleButton)({
  borderRadius: '10px',
  padding: '4px 6px',
  textTransform: 'none',
})

const UsersPanel = ({
  users,
  loading,
  error,
  counts,
  countsLoading,
  totalCount,
  page,
  pageSize,
  matchFilter,
  onMatchFilterChange,
  searchText,
  onSearchChange,
  onPageChange,
  isSelecting,
  matchingUserIds,
  draggedImageName,
  onMapPhoto,
  onUnlinkPhoto
}: UsersPanelProps) => {
  const theme = useTheme();
  const listRef = useRef<HTMLDivElement>(null);
  // The row currently under a dragged photo, highlighted as the active drop target.
  const [dragOverUserId, setDragOverUserId] = useState<number | null>(null);

  const styles = useMemo(
    () =>
      ({
        card: {
          flex: 1,
          borderRadius: 2,
          minHeight: 0,
          height: '100%',
          pt: 2,
          pb: 0,
          display: 'flex',
          flexDirection: 'column'
        },
        // Header row: title/summary on the left, match filter on the right
        header: {
          px: 2,
          mb: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        },
        // Locks the summary row height so swapping the loader/text doesn't jump
        summary: {
          display: 'flex',
          alignItems: 'center',
          minHeight: 16,
          marginTop: '4px',
        },
        // Full-width search bar below the header
        search: {
          flexShrink: 0,
          mb: 1,
          mx: 2,
        },
        searchIcon: {
          color: theme.palette.text.secondary
        },
        list: {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pt: 1,
        },
        pagination: {
          flexShrink: 0,
          // mt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          '& .MuiTablePagination-toolbar': { paddingLeft: 0 }
        },
        listItem: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderRadius: '10px',
          mx: 1,
          p: 1,
          transition: 'background-color 0.2s ease-in-out'
        },
        // Applied while an image is selected: the whole row becomes a click target
        listItemSelectable: {
          cursor: 'pointer',
          '&:hover': { backgroundColor: theme.palette.action.hover }
        },
        // Applied while a match request is in flight for this row
        listItemMatching: {
          backgroundColor: theme.palette.green[50]
        },
        // The row a dragged photo is hovering over — the active drop target.
        // Inset box-shadow draws the ring inside the row's box (no extra width,
        // so the row never reflows when it activates) and shows all four sides.
        listItemDropTarget: {
          backgroundColor: theme.palette.blue[50],
          boxShadow: `inset 0 0 0 1px ${theme.palette.blue[300]}`
        },
        // While a photo is being dragged, let drag events fall through the row's
        // children (avatar, text, chip) so the entire row is one solid drop zone —
        // otherwise children intercept dragenter/leave and only the padding hits.
        listItemDraggedOver: {
          '& > *': { pointerEvents: 'none' }
        },
        mapPhotoButton: { flexShrink: 0 },
        matchingLoader: {
          flexShrink: 0,
          mr: 1,
          color: theme.palette.text.secondary
        },
        // Avatar + name/email grouped on the left
        identity: {
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minWidth: 0
        },
        avatar: {
          width: 36,
          height: 36,
          fontSize: 14,
          bgcolor: theme.palette.blueGrey[200],
          color: theme.palette.text.primary
        },
        text: { minWidth: 0 },
        statusChip: { fontWeight: 700 },
        // 'Mapped' chip + unlink button, grouped at the end of the row
        mappedActions: {
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        },
        unlinkButton: {
          color: theme.palette.error.main,
          p: 0.5
        },
        toggleButton: {
          textTransform: 'none',
        },
      }) satisfies Record<string, SxProps<Theme>>,
    [theme]
  );

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, value: MatchFilter | null) => {
    if (value !== null) onMatchFilterChange(value);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const summarySegments = [`${counts.all.toLocaleString()} total`];
  if (matchFilter !== 'unmatched') {
    summarySegments.push(`${counts.withPhoto.toLocaleString()} mapped`);
  }
  if (matchFilter !== 'matched') {
    summarySegments.push(`${counts.without.toLocaleString()} unmapped`);
  }
  const summaryText = summarySegments.join(' · ');

  return (
    <Card variant='outlined' sx={styles.card}>
      <Box sx={styles.header}>
        <Box>
          <Typography variant='bodyMedium' sx={{
            fontWeight: '600'
          }}>
            Users
          </Typography>
          <Box sx={styles.summary}>
            {countsLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant='bodySmall' sx={{
                color: 'text.secondary'
              }}>
                {error ? error : summaryText}
              </Typography>
            )}
          </Box>
        </Box>

        {!error && (
          <ToggleButtonGroup
            size='small'
            exclusive
            value={matchFilter}
            onChange={handleFilterChange}
            aria-label='Filter users by match status'
          >
            <StyledToggleButton value='all'>All</StyledToggleButton>
            <StyledToggleButton value='unmatched'>Unmapped</StyledToggleButton>
            <StyledToggleButton value='matched'>Mapped</StyledToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {!error && (
        <TextInput
          name='users-search'
          placeholder='Search by name or email'
          size='small'
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          error={false}
          style={styles.search}
          startAdornment={
            <InputAdornment position='start'>
              <SearchIcon fontSize='small' sx={styles.searchIcon} />
            </InputAdornment>
          }
        />
      )}

      {!error && (!loading || users.length > 0) && (
        <>
          <Box ref={listRef} sx={styles.list}>
            {users.map((official) => {
              const isMapped = Boolean(official.img_url);
              const isMatching = matchingUserIds.includes(official.id);
              // Only unmapped users not already saving can receive a photo —
              // by click (an image is selected) or by drop (one is being dragged).
              const eligible = !isMapped && !isMatching;
              const selectable = isSelecting && eligible;
              const droppable = !!draggedImageName && eligible;
              const isDropTarget = droppable && dragOverUserId === official.id;
              return (
                <Box
                  key={official.id}
                  sx={[
                    styles.listItem,
                    selectable && styles.listItemSelectable,
                    isMatching && styles.listItemMatching,
                    droppable && styles.listItemDraggedOver,
                    isDropTarget && styles.listItemDropTarget
                  ]}
                  onClick={selectable ? () => onMapPhoto(official.id) : undefined}
                  onDragOver={
                    droppable
                      ? (e) => {
                          // Required for the row to be a valid drop target.
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      : undefined
                  }
                  onDragEnter={droppable ? () => setDragOverUserId(official.id) : undefined}
                  onDragLeave={
                    droppable
                      ? () => setDragOverUserId((prev) => (prev === official.id ? null : prev))
                      : undefined
                  }
                  onDrop={
                    droppable
                      ? (e) => {
                          e.preventDefault();
                          setDragOverUserId(null);
                          // Prefer the name carried by the drag; fall back to the
                          // parent's tracked dragged image.
                          const name = e.dataTransfer.getData('text/plain') || draggedImageName;
                          onMapPhoto(official.id, name || undefined);
                        }
                      : undefined
                  }
                >
                  <Box sx={styles.identity}>
                    <Avatar src={official.img_url || undefined} sx={styles.avatar}>
                      {Utils.getInitials(official.first_name, official.last_name)}
                    </Avatar>
                    <Box sx={styles.text}>
                      <Typography variant='bodySmall' noWrap sx={{
                        fontWeight: '600'
                      }}>
                        {Utils.fullName(official.first_name, official.last_name)}
                      </Typography>
                      <Typography variant='bodySmall' noWrap sx={{
                        color: 'text.secondary'
                      }}>
                        {official.email}
                      </Typography>
                    </Box>
                  </Box>
                  {isMatching ? (
                    <CircularProgress size={20} sx={styles.matchingLoader} />
                  ) : isMapped ? (
                    <Box sx={styles.mappedActions}>
                      <Chip label='Mapped' color='success' size='small' sx={styles.statusChip} />
                      <IconButton
                        aria-label={`Unlink photo from ${Utils.fullName(official.first_name, official.last_name)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnlinkPhoto(official);
                        }}
                        sx={styles.unlinkButton}
                      >
                        <LinkIcon />
                      </IconButton>
                    </Box>
                  ) : isSelecting || isDropTarget ? (
                    <Button
                      text='Map photo'
                      size='small'
                      startIcon={<LinkIcon
                        sx={{
                          fontSize: 'small',
                          marginRight: 1
                        }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMapPhoto(official.id);
                      }}
                      sx={styles.mapPhotoButton}
                    />
                  ) : (
                    <Chip label='No photo' size='small' sx={styles.statusChip} />
                  )}
                </Box>
              );
            })}
          </Box>
          <TablePagination
            component='div'
            count={totalCount}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            onPageChange={handlePageChange}
            sx={styles.pagination}
          />
        </>
      )}
    </Card>
  );
};

export default UsersPanel;
